import { createContext, useCallback, useContext, useEffect, useState } from 'react'

type HoldPlanSectionOpen = () => () => void

/** Given by the plan sections bar; absent where a section stands on the page itself. */
export const PlanSectionBusyContext = createContext<HoldPlanSectionOpen | null>(null)

/**
 * Whether a section in the dialog is saving, and the hold the sections take for it. A section
 * holds the dialog while its own loader shows, so the dialog is neither closed nor switched to
 * another section in the middle of a save.
 */
export const usePlanSectionBusy = () => {
  const [holds, setHolds] = useState(0)

  const holdOpen = useCallback<HoldPlanSectionOpen>(() => {
    setHolds((count) => count + 1)

    let isReleased = false

    return () => {
      if (!isReleased) {
        isReleased = true
        setHolds((count) => count - 1)
      }
    }
  }, [])

  return { isBusy: holds > 0, holdOpen }
}

/** Keeps the plan section dialog open while `isSaving`; does nothing outside the dialog. */
export const useHoldPlanSectionOpen = (isSaving: boolean) => {
  const holdOpen = useContext(PlanSectionBusyContext)

  useEffect(() => (isSaving && holdOpen ? holdOpen() : undefined), [holdOpen, isSaving])
}
